import { useState, useRef, ChangeEvent } from 'react';
import { getUploadUrl, uploadFile } from '../../services/galleryApi';

interface ImageUploadProps {
  userId: string;
  onUploadComplete?: () => void;
}

export function ImageUpload({ userId, onUploadComplete }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  async function handleUpload(file: File) {
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setProgress(0);

      // Get presigned POST URL and fields
      const { uploadUrl, uploadFields } = await getUploadUrl(userId, file.name, file.type);

      // Upload file
      await uploadFile(uploadUrl, uploadFields, file, setProgress);

      // Reset state
      setProgress(100);
      setTimeout(() => {
        setUploading(false);
        setProgress(0);
        onUploadComplete?.();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setUploading(false);
      setProgress(0);
    }
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedTypes.join(',')}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${dragOver ? 'var(--primary-600)' : 'var(--line-strong)'}`,
          borderRadius: '8px',
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          backgroundColor: dragOver ? 'var(--primary-100)' : 'var(--surface-2)',
          transition: 'all 0.2s',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          minHeight: '200px',
        }}
      >
        {uploading ? (
          <div style={{ padding: '16px', width: '80%' }}>
            <div style={{ marginBottom: '12px', color: 'var(--ink-600)', fontSize: '14px' }}>
              Uploading... {progress}%
            </div>
            <div
              style={{
                width: '100%',
                height: '6px',
                backgroundColor: 'var(--line-soft)',
                borderRadius: '3px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  backgroundColor: 'var(--success-500)',
                  transition: 'width 0.2s',
                }}
              />
            </div>
          </div>
        ) : error ? (
          <div style={{ padding: '16px' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚠️</div>
            <div style={{ color: 'var(--danger-700)', fontSize: '12px', marginBottom: '8px' }}>
              {error}
            </div>
            <div style={{ color: 'var(--ink-600)', fontSize: '12px' }}>
              Click to try again
            </div>
          </div>
        ) : (
          <div style={{ padding: '16px' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px', color: 'var(--primary-600)' }}>+</div>
            <div style={{ color: 'var(--ink-600)', fontSize: '14px', fontWeight: 500 }}>
              Add Asset
            </div>
            <div style={{ color: 'var(--ink-400)', fontSize: '11px', marginTop: '4px' }}>
              Drop a file or click to browse
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ImageUpload;
